import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, Search, ShoppingCart } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faGift } from '@fortawesome/free-solid-svg-icons';
import MessageBox from '../../components/MessageBox';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import rifaService from '../../services/rifa.service';
import ticketService from '../../services/ticket.service';
import { getError } from '../../utils';
import { paths } from '../../routes/paths';
import { trackAnalyticsEvent } from '../../lib/analytics';
import { mostrarNumerosOf, pickerModeOf, sorteosEnabledOf } from '../../lib/boardPlans';

const reservePath = paths.reserve;
const PAGE_SIZE = 80;
const MAX_TICKETS = 10;
const VISIBLE_PRIZES = 3;

const byPuesto = premios => [...premios].sort((a, b) => (a.puesto ?? 0) - (b.puesto ?? 0));

function PrizeList({ premios }) {
  return (
    <ul className='space-y-1'>
      {byPuesto(premios).map((premio, i) => (
        <li key={premio._id || i} className='flex items-start gap-2 text-sm'>
          <span className='shrink-0 font-bold text-zinc-400'>{premio.puesto}°</span>
          <span>
            {premio.nombre && <span className='font-semibold text-ink'>{premio.nombre}</span>}
            {premio.descripcion && (
              <span className={premio.nombre ? ' text-zinc-500' : ''}>
                {premio.nombre ? ' — ' : ''}
                {premio.descripcion}
              </span>
            )}
            {!premio.nombre && !premio.descripcion && '—'}
          </span>
        </li>
      ))}
    </ul>
  );
}

function checkoutStateForRifa(rifaId, rifaNombre) {
  return { rifaId, rifaNombre: rifaNombre || '' };
}

function getCompraPrefs(rifaId) {
  try {
    return JSON.parse(sessionStorage.getItem(`compra:${rifaId}`)) || {};
  } catch {
    return {};
  }
}

function saveCompraPrefs(rifaId, prefs) {
  sessionStorage.setItem(`compra:${rifaId}`, JSON.stringify(prefs));
}

function TicketsRifaSkeleton() {
  return (
    <div className='min-h-screen bg-white font-sans'>
      <div className='flex items-center gap-3 px-4 py-4 lg:px-8 lg:py-5'>
        <Skeleton className='h-9 w-9 shrink-0 rounded-full' />
        <Skeleton className='h-5 w-48' />
      </div>
      <div className='mx-auto max-w-7xl px-4 lg:px-8'>
        <div className='lg:grid lg:grid-cols-5 lg:gap-10'>
          <div className='lg:col-span-2 space-y-3'>
            <Skeleton className='w-full aspect-video rounded-2xl' />
            <div className='rounded-2xl bg-zinc-100 p-4 space-y-3'>
              <Skeleton className='h-6 w-3/4' />
              <Skeleton className='h-4 w-1/3' />
              <div className='space-y-1.5'>
                <Skeleton className='h-4 w-full' />
                <Skeleton className='h-4 w-full' />
              </div>
              <Skeleton className='h-2 w-full rounded-full' />
              <Skeleton className='h-8 w-1/3' />
            </div>
            <Skeleton className='hidden lg:block h-12 w-full rounded-xl' />
          </div>
          <div className='lg:col-span-3 mt-5 lg:mt-0 space-y-4'>
            <Skeleton className='h-24 w-full rounded-xl' />
            <Skeleton className='h-11 w-full rounded-xl' />
            <Skeleton className='h-[min(520px,calc(100vh-20rem))] w-full rounded-xl' />
          </div>
        </div>
      </div>
    </div>
  );
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export default function TicketsRifa() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [{ loading, error }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
  });

  const locationRef = useRef(location.state);
  const savedPrefs = getCompraPrefs(id);
  const restoredTickets = locationRef.current?.selectedTickets;

  const [rifaDetails, setRifaDetails] = useState(null);
  const [ticketStats, setTicketStats] = useState(null);
  const [visibleTickets, setVisibleTickets] = useState([]);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsLoadingMore, setTicketsLoadingMore] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState(() => restoredTickets || []);
  const [selectionMode, setSelectionMode] = useState(() =>
    restoredTickets?.length ? 'manual' : savedPrefs.selectionMode || 'manual'
  );
  const [randomCountInput, setRandomCountInput] = useState(() => {
    const savedCount = Number(savedPrefs.randomCount);
    return String(
      Number.isInteger(savedCount) && savedCount >= 1 && savedCount <= MAX_TICKETS ? savedCount : 1
    );
  });
  const lastValidRandomCount = useRef(Number(randomCountInput));
  const randomCount = /^(?:[1-9]|10)$/.test(randomCountInput) ? Number(randomCountInput) : null;
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [buyingRandom, setBuyingRandom] = useState(false);
  const [showPrizes, setShowPrizes] = useState(false);
  const trackedRaffleRef = useRef(null);

  const scrollContainerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const pageRef = useRef(0);
  const hasMoreRef = useRef(true);

  useEffect(() => {
    saveCompraPrefs(id, {
      selectionMode,
      randomCount: randomCount ?? lastValidRandomCount.current,
    });
  }, [id, selectionMode, randomCount]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const [rifaData, stats] = await Promise.all([
          rifaService.getRifa(id),
          ticketService.getRifaTicketStats(id),
        ]);
        setRifaDetails(rifaData);
        if (trackedRaffleRef.current !== id) {
          trackedRaffleRef.current = id;
          trackAnalyticsEvent('raffle_view', { rifaId: id, stage: 'browse' });
        }
        setTicketStats(stats);
        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    fetchData();
  }, [id]);

  const enrichTicket = useCallback(
    ticket => ({
      ...ticket,
      precioTotal: Number(rifaDetails?.precioTicket) || Number(ticket.precioTotal) || 0,
    }),
    [rifaDetails]
  );

  const loadTicketPage = useCallback(
    async (page, search, replace = false) => {
      if (selectionMode !== 'manual') return;
      const skip = page * PAGE_SIZE;
      const loadingSetter = page === 0 ? setTicketsLoading : setTicketsLoadingMore;
      loadingSetter(true);
      try {
        const { tickets, total } = await ticketService.getAvailableTicketsPaginated(
          id,
          skip,
          PAGE_SIZE,
          search
        );
        const enriched = tickets.map(enrichTicket);
        setVisibleTickets(prev => (replace ? enriched : [...prev, ...enriched]));
        setTicketsTotal(total);
        hasMoreRef.current = skip + enriched.length < total;
        pageRef.current = page;
      } catch (err) {
        if (page === 0) {
          setVisibleTickets([]);
          setTicketsTotal(0);
        }
        toast.error(getError(err));
      } finally {
        loadingSetter(false);
      }
    },
    [id, enrichTicket, selectionMode]
  );

  useEffect(() => {
    if (loading || selectionMode !== 'manual') return;
    pageRef.current = 0;
    hasMoreRef.current = true;
    loadTicketPage(0, debouncedSearch, true);
  }, [loading, selectionMode, debouncedSearch, loadTicketPage]);

  useEffect(() => {
    if (selectionMode !== 'manual' || !hasMoreRef.current) return;
    const root = scrollContainerRef.current;
    const target = loadMoreRef.current;
    if (!root || !target) return;

    const observer = new IntersectionObserver(
      entries => {
        if (
          entries[0]?.isIntersecting &&
          hasMoreRef.current &&
          !ticketsLoading &&
          !ticketsLoadingMore
        ) {
          loadTicketPage(pageRef.current + 1, debouncedSearch, false);
        }
      },
      { root, rootMargin: '120px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [
    selectionMode,
    debouncedSearch,
    loadTicketPage,
    ticketsLoading,
    ticketsLoadingMore,
    visibleTickets.length,
  ]);

  const toggleTicket = useCallback(
    ticket => {
      if (ticket.estado !== 'Disponible' || selectionMode !== 'manual') return;
      setSelectedTickets(prev => {
        const exists = prev.some(t => t._id === ticket._id);
        if (exists) return prev.filter(t => t._id !== ticket._id);
        if (prev.length >= MAX_TICKETS) {
          toast.warning(t('ticket.maxTicketsPerTransaction'));
          return prev;
        }
        return [...prev, enrichTicket(ticket)];
      });
    },
    [selectionMode, t, enrichTicket]
  );

  const handleSelectionModeChange = mode => {
    setSelectionMode(mode);
    if (mode === 'random') {
      setSearchQuery('');
      setVisibleTickets([]);
      setSelectedTickets([]);
    }
  };

  // The raffle may allow only one picking mode: it overrides restored/saved preferences
  // (hidden numbers always mean random: the buyer only chooses a quantity)
  const allowedPickerMode = pickerModeOf(rifaDetails);
  const numerosOcultos = Boolean(rifaDetails) && !mostrarNumerosOf(rifaDetails);
  useEffect(() => {
    if (!rifaDetails || allowedPickerMode === 'both') return;
    if (selectionMode !== allowedPickerMode) handleSelectionModeChange(allowedPickerMode);
  }, [rifaDetails, allowedPickerMode, selectionMode]);

  const handleRandomCountChange = event => {
    const next = event.target.value;
    if (!/^\d*$/.test(next)) return;
    setRandomCountInput(next);
    if (/^(?:[1-9]|10)$/.test(next)) lastValidRandomCount.current = Number(next);
  };

  const normalizeRandomCount = () => {
    if (!randomCountInput) {
      setRandomCountInput(String(lastValidRandomCount.current));
      return;
    }
    const value = Number(randomCountInput);
    const normalized = Number.isFinite(value)
      ? Math.min(MAX_TICKETS, Math.max(1, value))
      : lastValidRandomCount.current;
    lastValidRandomCount.current = normalized;
    setRandomCountInput(String(normalized));
  };

  const handleBuy = async () => {
    if (selectionMode === 'random') {
      if (randomCount === null) {
        toast.error(t('ticket.maxTicketsPerTransaction'));
        return;
      }
      // Hidden numbers: only the quantity travels to checkout; the server assigns the numbers
      if (numerosOcultos) {
        const unit = Number(rifaDetails?.precioTicket) || 0;
        const tickets = Array.from({ length: randomCount }, (_, i) => ({
          _id: `oculto-${i}`,
          nroSerial: '',
          precioTotal: unit,
          oculto: true,
          rifa: id,
        }));
        trackAnalyticsEvent('ticket_selection', {
          rifaId: id,
          ticketCount: tickets.length,
          stage: 'selection',
        });
        navigate(reservePath, {
          state: {
            tickets,
            numerosOcultos: true,
            ...checkoutStateForRifa(id, rifaDetails?.nombre),
          },
          replace: true,
        });
        return;
      }
      try {
        setBuyingRandom(true);
        const picked = await ticketService.pickRandomAvailableTickets(id, randomCount);
        const tickets = picked.map(enrichTicket);
        trackAnalyticsEvent('ticket_selection', {
          rifaId: id,
          ticketCount: tickets.length,
          stage: 'selection',
        });
        navigate(reservePath, {
          state: { tickets, ...checkoutStateForRifa(id, rifaDetails?.nombre) },
          replace: true,
        });
      } catch (err) {
        toast.error(getError(err));
      } finally {
        setBuyingRandom(false);
      }
      return;
    }

    if (selectedTickets.length === 0) {
      toast.error(t('ticket.selectAtLeastOne'));
      return;
    }
    trackAnalyticsEvent('ticket_selection', {
      rifaId: id,
      ticketCount: selectedTickets.length,
      stage: 'selection',
    });
    navigate(reservePath, {
      state: { tickets: selectedTickets, ...checkoutStateForRifa(id, rifaDetails?.nombre) },
      replace: true,
    });
  };

  const isSelected = ticket => selectedTickets.some(t => t._id === ticket._id);
  const isManualMode = selectionMode === 'manual';
  const pickerDisabled = !isManualMode;

  const selectedCount = isManualMode ? selectedTickets.length : (randomCount ?? 0);
  const precio = Number(rifaDetails?.precioTicket) || null;
  const totalPrice = precio ? selectedCount * Number(precio) : 0;

  const soldCount = ticketStats?.vendido ?? 0;
  const onlineAvailable = ticketStats?.disponible ?? 0;
  const totalTickets = rifaDetails?.totalTickets ?? ticketStats?.totalTickets ?? 0;
  const soldPercent = ticketStats?.soldPercent ?? 0;

  // Bonos without draws: no sorteo dates, prizes or prize popup
  const sorteos = sorteosEnabledOf(rifaDetails) ? rifaDetails?.sorteos || [] : [];
  const now = new Date();
  const nextSorteo =
    sorteos.find(s => new Date(s.fecha) > now) || sorteos[sorteos.length - 1] || null;
  const totalPrizes = sorteos.reduce((acc, s) => acc + (s.premios?.length || 0), 0);

  if (loading) return <TicketsRifaSkeleton />;
  if (error) return <MessageBox variant='danger'>{error}</MessageBox>;

  const buyButton = (extraClass = '') => (
    <button
      type='button'
      onClick={handleBuy}
      disabled={buyingRandom || (selectionMode === 'random' && randomCount === null)}
      className={`flex w-full items-center justify-between rounded-xl bg-ink px-4 py-3 font-semibold text-white transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-60 ${extraClass}`}
    >
      <div className='flex items-center gap-2'>
        {buyingRandom ? (
          <Loader2 className='h-5 w-5 animate-spin' />
        ) : (
          <ShoppingCart className='h-5 w-5' />
        )}
        <span>
          {selectedCount === 0
            ? 'Comprar números'
            : `Total (${selectedCount} número${selectedCount !== 1 ? 's' : ''})`}
        </span>
      </div>
      <div className='flex items-center gap-3'>
        {totalPrice > 0 && <span className='font-bold'>${totalPrice}</span>}
        <span className='opacity-70'>→</span>
      </div>
    </button>
  );

  return (
    <div className='min-h-screen bg-white font-sans text-ink'>
      <div className='flex items-center gap-3 px-4 py-4 lg:px-8 lg:py-5'>
        <button
          onClick={() => navigate(-1)}
          className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface'
          aria-label='Volver'
        >
          <ArrowLeft className='h-4 w-4' />
        </button>
        <h1 className='truncate text-base font-semibold lg:text-lg'>{rifaDetails?.nombre}</h1>
      </div>

      <div className='mx-auto max-w-7xl px-4 pb-28 lg:px-8 lg:pb-12'>
        <div className='lg:grid lg:grid-cols-5 lg:items-start lg:gap-10'>
          <div className='lg:col-span-2 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto'>
            <div className='overflow-hidden rounded-2xl bg-surface'>
              {rifaDetails?.rifaImage?.fileLink && (
                <img
                  src={rifaDetails.rifaImage.fileLink}
                  alt={rifaDetails.nombre}
                  className='w-full aspect-video object-cover'
                />
              )}
              <div className='p-4 space-y-3'>
                <h2 className='text-xl font-bold leading-snug'>{rifaDetails?.nombre}</h2>

                {nextSorteo?.fecha && (
                  <p className='flex items-center gap-2 text-sm text-zinc-500'>
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      className='h-4 w-4 shrink-0 text-zinc-400'
                      aria-hidden='true'
                    />
                    <span>
                      {t('raffle.draw')}:{' '}
                      <span className='font-medium text-ink'>
                        {moment(nextSorteo.fecha).format('DD/MM/YYYY')}
                      </span>
                    </span>
                  </p>
                )}

                {nextSorteo?.premios?.length > 0 && (
                  <div>
                    <p className='mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-zinc-400'>
                      <FontAwesomeIcon
                        icon={faGift}
                        className='h-4 w-4 shrink-0 text-zinc-400'
                        aria-hidden='true'
                      />
                      {t('raffle.prizes')}
                    </p>
                    <PrizeList premios={byPuesto(nextSorteo.premios).slice(0, VISIBLE_PRIZES)} />
                    {totalPrizes > VISIBLE_PRIZES || sorteos.length > 1 ? (
                      <button
                        type='button'
                        onClick={() => setShowPrizes(true)}
                        className='mt-2 text-sm font-semibold text-ink underline underline-offset-2 hover:opacity-70'
                      >
                        {t('raffle.seeAllPrizes', { count: totalPrizes })}
                      </button>
                    ) : null}
                  </div>
                )}

                <Dialog open={showPrizes} onOpenChange={setShowPrizes}>
                  <DialogContent className='max-h-[85vh] w-[calc(100%-2rem)] overflow-y-auto rounded-2xl sm:max-w-lg'>
                    <DialogHeader>
                      <DialogTitle>{t('raffle.prizes')}</DialogTitle>
                    </DialogHeader>
                    <div className='space-y-5'>
                      {sorteos.map((sorteo, i) => (
                        <div key={sorteo._id || i} className='space-y-2'>
                          <div className='flex items-baseline justify-between gap-3 border-b border-divider pb-1'>
                            <p className='font-semibold text-ink'>{sorteo.titulo}</p>
                            {sorteo.fecha && (
                              <span className='shrink-0 text-xs text-zinc-500'>
                                {moment(sorteo.fecha).format('DD/MM/YYYY')}
                              </span>
                            )}
                          </div>
                          {sorteo.premios?.length > 0 ? (
                            <PrizeList premios={sorteo.premios} />
                          ) : (
                            <p className='text-sm text-zinc-400'>{t('raffle.noPrizes')}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>

                <div className='space-y-1.5'>
                  <div className='flex items-center justify-between text-xs text-zinc-500'>
                    <span className='font-medium text-zinc-600'>
                      {t('ticket.digitalSaleProgress')}
                    </span>
                    <span>{soldPercent}%</span>
                  </div>
                  <div className='h-2 w-full overflow-hidden rounded-full bg-divider'>
                    <div
                      className='h-full rounded-full bg-ink transition-all duration-500'
                      style={{ width: `${soldPercent}%` }}
                    />
                  </div>
                  <div className='flex flex-wrap justify-between gap-x-3 gap-y-1 text-xs text-zinc-500'>
                    <span>{t('ticket.soldCount', { count: soldCount })}</span>
                    <span>{t('ticket.onlineAvailableCount', { count: onlineAvailable })}</span>
                    <span className='font-medium text-ink'>
                      {t('ticket.totalTicketsCount', { count: totalTickets })}
                    </span>
                  </div>
                  <p className='text-[11px] leading-snug text-zinc-400'>
                    {t('ticket.digitalSaleProgressHint')}
                  </p>
                </div>

                {precio != null && (
                  <div className='flex items-baseline gap-2'>
                    <span className='text-sm text-zinc-500'>Precio por número:</span>
                    <span className='text-xl font-bold'>${precio}</span>
                  </div>
                )}
              </div>
            </div>

            <div className='mt-4 hidden lg:block'>{buyButton()}</div>
          </div>

          <div className='lg:col-span-3 mt-5 lg:mt-0 space-y-5'>
            <>
              <div className='rounded-xl border border-divider bg-surface p-4 space-y-4'>
                {allowedPickerMode === 'both' && (
                  <p className='text-sm font-semibold text-ink'>{t('ticket.selectionModeLabel')}</p>
                )}
                {allowedPickerMode === 'manual' && (
                  <p className='text-sm font-semibold text-ink'>
                    {t('ticket.selectionModeManual')}
                  </p>
                )}
                {allowedPickerMode === 'random' && (
                  <p className='text-sm font-semibold text-ink'>
                    {numerosOcultos
                      ? t('ticket.hiddenNumbersTitle')
                      : t('ticket.selectionModeRandom')}
                  </p>
                )}
                {allowedPickerMode === 'both' && (
                  <div className='flex flex-col gap-3 sm:flex-row sm:gap-6'>
                    <label className='flex cursor-pointer items-center gap-2 text-sm'>
                      <input
                        type='radio'
                        name='selectionMode'
                        value='manual'
                        checked={selectionMode === 'manual'}
                        onChange={() => handleSelectionModeChange('manual')}
                        className='h-4 w-4 accent-ink'
                      />
                      {t('ticket.selectionModeManual')}
                    </label>
                    <label className='flex cursor-pointer items-center gap-2 text-sm'>
                      <input
                        type='radio'
                        name='selectionMode'
                        value='random'
                        checked={selectionMode === 'random'}
                        onChange={() => handleSelectionModeChange('random')}
                        className='h-4 w-4 accent-ink'
                      />
                      {t('ticket.selectionModeRandom')}
                    </label>
                  </div>
                )}

                {selectionMode === 'random' && (
                  <div
                    className={`space-y-2 ${
                      allowedPickerMode === 'both' ? 'border-t border-divider pt-4' : ''
                    }`}
                  >
                    <label htmlFor='randomCount' className='block text-sm font-medium text-ink'>
                      {t('ticket.randomCountLabel')}
                    </label>
                    <input
                      id='randomCount'
                      type='text'
                      inputMode='numeric'
                      pattern='[0-9]*'
                      value={randomCountInput}
                      aria-invalid={randomCount === null}
                      onFocus={event => event.currentTarget.select()}
                      onChange={handleRandomCountChange}
                      onBlur={normalizeRandomCount}
                      className='w-full max-w-[120px] rounded-xl border border-divider bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ink/20'
                    />
                    <p className='text-xs text-zinc-500'>
                      {numerosOcultos ? t('ticket.hiddenNumbersHint') : t('ticket.randomCountHint')}
                    </p>
                  </div>
                )}
              </div>

              {allowedPickerMode !== 'random' && (
                <div
                  className={
                    pickerDisabled ? 'pointer-events-none space-y-5 opacity-45' : 'space-y-5'
                  }
                >
                  {pickerDisabled && (
                    <p className='text-sm text-zinc-500'>{t('ticket.randomPickerDisabled')}</p>
                  )}

                  <div className='relative'>
                    <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400' />
                    <input
                      type='number'
                      placeholder={t('ticket.enterNumberToReserve')}
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      disabled={pickerDisabled}
                      className='w-full rounded-xl border border-divider bg-surface py-2.5 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-ink/20 disabled:cursor-not-allowed'
                    />
                  </div>

                  {selectedTickets.length > 0 && isManualMode && (
                    <div>
                      <p className='mb-2 text-sm font-semibold'>
                        {t('ticket.selectedNumbers', { count: selectedTickets.length })}
                      </p>
                      <div className='flex flex-wrap gap-2'>
                        {selectedTickets.map(ticket => (
                          <button
                            key={ticket._id}
                            type='button'
                            onClick={() => toggleTicket(ticket)}
                            className='flex h-9 w-14 items-center justify-center rounded-lg bg-ink text-sm font-semibold text-white transition-opacity hover:opacity-80'
                          >
                            {ticket.nroSerial}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className='mb-2 flex items-center justify-between gap-2'>
                      <p className='text-sm font-semibold'>{t('ticket.availableNumbers')}</p>
                      {isManualMode && ticketsTotal > 0 && (
                        <span className='text-xs text-zinc-400'>
                          {visibleTickets.length}/{ticketsTotal}
                        </span>
                      )}
                    </div>

                    {isManualMode && ticketsLoading ? (
                      <div className='flex h-[min(520px,calc(100vh-20rem))] items-center justify-center rounded-xl border border-divider bg-surface'>
                        <Loader2 className='h-6 w-6 animate-spin text-zinc-400' />
                      </div>
                    ) : isManualMode && visibleTickets.length === 0 ? (
                      <p className='text-sm text-zinc-400'>No hay números disponibles.</p>
                    ) : isManualMode ? (
                      <div
                        ref={scrollContainerRef}
                        className='h-[min(520px,calc(100vh-20rem))] overflow-y-auto rounded-xl border border-divider bg-surface p-3'
                      >
                        <div className='grid grid-cols-6 gap-1.5 sm:grid-cols-8 lg:grid-cols-8 xl:grid-cols-10'>
                          {visibleTickets.map(ticket => {
                            const sel = isSelected(ticket);
                            return (
                              <button
                                key={ticket._id}
                                type='button'
                                onClick={() => toggleTicket(ticket)}
                                className={`h-9 rounded-lg text-sm font-semibold transition-colors ${
                                  sel
                                    ? 'bg-ink text-white'
                                    : 'border border-divider bg-white text-ink hover:border-ink'
                                }`}
                              >
                                {ticket.nroSerial}
                              </button>
                            );
                          })}
                        </div>
                        <div ref={loadMoreRef} className='h-6' aria-hidden='true' />
                        {ticketsLoadingMore && (
                          <div className='flex justify-center py-3'>
                            <Loader2 className='h-5 w-5 animate-spin text-zinc-400' />
                            <span className='sr-only'>{t('ticket.loadingMoreNumbers')}</span>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </>
          </div>
        </div>
      </div>

      <div className='fixed bottom-0 left-0 right-0 z-30 border-t border-divider bg-white px-4 py-3 lg:hidden'>
        {buyButton()}
      </div>
    </div>
  );
}
